/**
 * MappingStudio — the new lovable-style sync configuration UI.
 *
 * This component replaces the old ProviderNewFlow tab-based interface with the
 * Mapping Studio design from the lovable app. It preserves ALL existing
 * backend wiring (sync configuration CRUD, field mapping, integration.app flows)
 * while providing the cleaner, more intuitive UI.
 *
 * Architecture:
 * - All state management and API logic lives here (migrated from ProviderNewFlow)
 * - Sub-components (ObjectTab, SimpleObjectTab) handle per-object UI
 * - Existing API-connected components (FieldMappingTable, RoleMappingTable, etc.)
 *   are composed inside ObjectTab
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal, Stack, Text, Group } from "@mantine/core";
import { toast } from "sonner";
import { useIntegrationApp } from "@membranehq/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Info, Settings2 } from "lucide-react";

import { getProvider, type ProviderKey } from "../../../../integrations/providers";
import type { ProviderObjectSlug } from "../../../../integrations/providers/types";
import {
  useAllIntegrations,
  useWorkspaceObjects,
  useSyncConfigurations,
  useCreateSyncConfiguration,
  useCreateWriteBackSync,
  useUpdateSyncConfiguration,
  useDeleteSyncConfiguration,
} from "../../../../hooks/QueryHooks";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import { CompanyLogo, getDomainFromCompany } from "../../../../components/ui/CompanyLogo";

import type {
  ConfigByObject,
  DraftMappingsByObject,
  WritebackByObject,
  RoleMappingsByObject,
  RelationshipMappingsByObject,
  WritebackFieldMapping,
} from "../../hubspot/new-flow/types";
import type {
  FieldDefinition,
  HubSpotField,
} from "../../../../services/syncConfiguration/syncConfigurationEndpoints.interface";

import { RelationshipDiagram } from "./RelationshipDiagram";
import { SimpleObjectTab } from "./SimpleObjectTab";
import { ObjectTab } from "./ObjectTab";
import type { MappingConfig, ObjectConfig } from "./types";
import { getDefaultConfig } from "./types";
import {
  extractSyncConfigurations,
  normalizeSyncDirection,
  normalizeProviderSyncConfigurations,
  toBackendSlug,
  toUiSlug,
} from "./adapters";
import type { CommandResult } from "./commandTypes";
import { logMappingStudioEvent } from "./logger";
import { runWithObjectMutex } from "./objectMutex";
import {
  createBulkFieldMappings,
  deleteFieldMapping,
  getFieldMappings,
  getObjectFields as getSyncObjectFieldsApi,
  updateFieldMapping,
} from "../../../../services/syncConfiguration/syncConfigurationEndpoints";
import { commitObject } from "./persistence/commitObject";
import { buildObjectReservations, validateObjectDraft } from "./persistence/validateDraft";

// ─── Props ──────────────────────────────────────────────────────────────────

export interface MappingStudioProps {
  providerKey: ProviderKey;
  isConnected: boolean;
  connectionId?: string;
  onBack: () => void;
  onComplete?: () => void;
  singleSyncCta?: boolean;
  showDeleteActions?: boolean;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function MappingStudio({
  providerKey,
  isConnected,
  connectionId,
  onBack,
  onComplete,
  singleSyncCta = false,
  showDeleteActions = true,
}: MappingStudioProps) {
  const integrationApp = useIntegrationApp();
  const provider = getProvider(providerKey);
  const activatableObjects = useMemo(
    () => [...(provider.activatableObjects || [])],
    [provider.activatableObjects],
  );
  const getDefaultSourceCollection = useCallback(
    (slug: string) => {
      return provider.defaultSourceCollections?.[slug as ProviderObjectSlug] || slug;
    },
    [provider.defaultSourceCollections],
  );
  const syncConfigBackedObjects = useMemo(
    () => new Set(["accounts", "contacts", "companies"]),
    [],
  );
  const workspace: any = useWorkspaceStore((s: any) => s.workspace);
  const queryClient = useQueryClient();

  // ─── Data fetching ──────────────────────────────────────────────────────

  const { data: allIntegrations } = useAllIntegrations({ workspaceId: workspace?.id });
  const { data: workspaceObjects } = useWorkspaceObjects({ workspaceId: workspace?.id });

  const createSyncConfigMutation = useCreateSyncConfiguration();
  const createWriteBackSyncMutation = useCreateWriteBackSync();
  const updateSyncConfigMutation = useUpdateSyncConfiguration();
  const deleteSyncConfigMutation = useDeleteSyncConfiguration();

  const integrationId = useMemo(() => {
    if (!Array.isArray(allIntegrations)) return undefined;
    return allIntegrations.find((i: any) => i?.source_name === provider.key)?.id;
  }, [allIntegrations, provider.key]);
  const providerIntegrationIds = useMemo(() => {
    if (!Array.isArray(allIntegrations)) return new Set<string>();
    return new Set(
      allIntegrations
        .filter((integration: any) => integration?.source_name === provider.key && integration?.id)
        .map((integration: any) => integration.id as string),
    );
  }, [allIntegrations, provider.key]);

  const { data: syncConfigurations }: any = useSyncConfigurations({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id && !!integrationId,
  } as any);

  const providerSyncConfigurations = useMemo(() => {
    return normalizeProviderSyncConfigurations({
      payload: syncConfigurations,
      integrationId,
    });
  }, [syncConfigurations, integrationId]);

  const workspaceIdBySlug = useMemo(() => {
    const map: Record<string, string> = {};
    (workspaceObjects ?? []).forEach((o: any) => {
      if (o?.api_slug && o?.id) {
        if (o.api_slug === "users") {
          map.contacts = o.id;
        } else if (o.api_slug === "conversation_messages") {
          map.emails = o.id;
        } else {
          map[o.api_slug] = o.id;
        }
      }
    });
    return map;
  }, [workspaceObjects]);

  // ─── Mapping Studio local state ─────────────────────────────────────────

  const [mappingConfig, setMappingConfig] = useState<MappingConfig>(() => {
    const initial = getDefaultConfig(providerKey, provider.label, integrationId);
    return {
      ...initial,
      objects: initial.objects.filter((obj) => activatableObjects.includes(obj.key as any)),
    };
  });
  const [activeTab, setActiveTab] = useState<string>(activatableObjects[0] || "accounts");

  // ProviderNewFlow-compatible state (needed by existing sub-components)
  const [configByObject, setConfigByObject] = useState<ConfigByObject>({});
  const [mappingsByObject, setMappingsByObject] = useState<DraftMappingsByObject>({});
  const [writebackByObject, setWritebackByObject] = useState<WritebackByObject>({});
  const [writebackMappingsByObject, setWritebackMappingsByObject] = useState<Record<string, WritebackFieldMapping[]>>({});
  const [roleMappingsByObject, setRoleMappingsByObject] = useState<RoleMappingsByObject>({});
  const [relationshipMappingsByObject, setRelationshipMappingsByObject] = useState<RelationshipMappingsByObject>({});
  const [localInboundSyncIdBySlug, setLocalInboundSyncIdBySlug] = useState<Record<string, string>>({});
  const [localWritebackSyncIdBySlug, setLocalWritebackSyncIdBySlug] = useState<Record<string, string>>({});
  const [activatingSyncs, setActivatingSyncs] = useState<Set<string>>(new Set());
  const [pendingSyncCreations, setPendingSyncCreations] = useState<Set<string>>(new Set());
  const [savingConfiguration, setSavingConfiguration] = useState(false);
  const [persistedObjectHashes, setPersistedObjectHashes] = useState<Record<string, string>>({});
  const snapshotInitializedRef = useRef(false);
  const [coreHydrationApplied, setCoreHydrationApplied] = useState(false);
  const nonCoreHydratedRef = useRef(false);
  const [nonCoreHydrationApplied, setNonCoreHydrationApplied] = useState(false);

  // Per-object provider fields state
  const [providerFieldsByObject, setProviderFieldsByObject] = useState<Record<string, HubSpotField[]>>({});
  const [providerFieldsLoadingByObject, setProviderFieldsLoadingByObject] = useState<Record<string, boolean>>({});
  const [providerCollectionsByObject, setProviderCollectionsByObject] = useState<Record<string, Array<{ key: string; name: string; fields: HubSpotField[] }>>>({});
  const [selectedCollectionByObject, setSelectedCollectionByObject] = useState<Record<string, string | null>>({});

  // Delete confirmation state
  const [deleteConfirmOpened, setDeleteConfirmOpened] = useState(false);
  const [syncToDelete, setSyncToDelete] = useState<string | null>(null);
  const commandLocksRef = useRef<Set<string>>(new Set());

  const runObjectCommand = useCallback(async <T,>(
    slug: string,
    commandName: string,
    run: () => Promise<T>,
  ): Promise<CommandResult<T>> => runWithObjectMutex({
    locks: commandLocksRef.current,
    slug,
    commandName,
    run,
    onStart: (targetSlug, targetCommand) => {
      logMappingStudioEvent("command:start", {
        provider: provider.key,
        slug: targetSlug,
        commandName: targetCommand,
      });
    },
    onSuccess: (targetSlug, targetCommand) => {
      logMappingStudioEvent("command:success", {
        provider: provider.key,
        slug: targetSlug,
        commandName: targetCommand,
      });
    },
    onError: (targetSlug, targetCommand, error, retriable) => {
      logMappingStudioEvent("command:error", {
        provider: provider.key,
        slug: targetSlug,
        commandName: targetCommand,
        error,
        retriable,
      });
    },
  }), [provider.key]);

  // Keep MappingStudio limited to provider-activatable objects.
  useEffect(() => {
    setMappingConfig((prev) => ({
      ...prev,
      objects: prev.objects.filter((obj) => activatableObjects.includes(obj.key as any)),
    }));
    setActiveTab((prev) => (activatableObjects.includes(prev as any) ? prev : activatableObjects[0] || "accounts"));
  }, [activatableObjects]);

  // ─── Derive sync IDs/statuses from existing syncs ───────────────────────

  const inboundSyncIdBySlug = useMemo(() => {
    const map: Record<string, string> = {};
    (providerSyncConfigurations || []).forEach((s: any) => {
      const slug = toUiSlug(s.destination_object);
      if (normalizeSyncDirection(s.sync_direction) === "write_in" && slug) map[slug] = s.id;
    });
    return { ...map, ...localInboundSyncIdBySlug };
  }, [providerSyncConfigurations, localInboundSyncIdBySlug]);

  const writebackSyncIdBySlug = useMemo(() => {
    const map: Record<string, string> = {};
    (providerSyncConfigurations || []).forEach((s: any) => {
      const slug = toUiSlug(s.destination_object);
      if (normalizeSyncDirection(s.sync_direction) === "write_out" && slug) map[slug] = s.id;
    });
    return { ...map, ...localWritebackSyncIdBySlug };
  }, [providerSyncConfigurations, localWritebackSyncIdBySlug]);

  // ─── Hydrate from existing sync configurations ──────────────────────────

  const hydratedRef = useRef(false);

  useEffect(() => {
    hydratedRef.current = false;
    setCoreHydrationApplied(false);
  }, [provider.key, integrationId, connectionId]);

  useEffect(() => {
    if (hydratedRef.current) return;
    if (!integrationId) return;
    if (typeof syncConfigurations === "undefined") return;

    const inboundConfigs = providerSyncConfigurations.filter(
      (s: any) => normalizeSyncDirection(s.sync_direction) === "write_in",
    );
    hydratedRef.current = true;
    setCoreHydrationApplied(true);
    if (!inboundConfigs.length) return;

    // Update mapping config objects with enabled state
    setMappingConfig((prev) => ({
      ...prev,
      integrationId,
      objects: prev.objects.map((obj) => {
        const sync = inboundConfigs.find(
          (s: any) => toUiSlug(s.destination_object) === obj.key,
        );
        if (!sync) return obj;
        const resolvedMatcherField = sync.matcher_field_name || sync.source_matcher_field || undefined;
        const usesDefaultExternalId = !resolvedMatcherField || resolvedMatcherField === "external_id";
        return {
          ...obj,
          enabled: true,
          syncMode: sync.allow_creates === false ? "update_only" as const : "create_and_update" as const,
          hasExternalId: usesDefaultExternalId ? false : true,
          externalIdField: usesDefaultExternalId ? null : resolvedMatcherField,
          sourceCollection: sync.source_object,
          inboundSyncId: sync.id,
          inboundSyncStatus: sync.status,
        };
      }),
    }));

    // Hydrate configByObject for existing sub-components
    const newConfig: ConfigByObject = {};
    inboundConfigs.forEach((sync: any) => {
      const slug = toUiSlug(sync.destination_object);
      if (!slug || !activatableObjects.includes(slug as any)) return;
      const resolvedMatcherField = sync.matcher_field_name || sync.source_matcher_field || undefined;
      const usesDefaultExternalId = !resolvedMatcherField || resolvedMatcherField === "external_id";
      newConfig[slug] = {
        enabled: sync.status === "enabled",
        sync_direction: "import_only",
        source_collection: sync.source_object,
        matching_method: usesDefaultExternalId ? "external_id" : "custom_field",
        matcher_field_name: usesDefaultExternalId ? undefined : resolvedMatcherField,
        hyperengage_matcher_field: "external_id",
        conflict_resolution: "hubspot_wins",
        allow_creates: sync.allow_creates ?? true,
        allow_updates: sync.allow_updates ?? true,
        sync_frequency: sync?.sync_policy?.update_frequency || "daily",
        time_window: sync?.time_window,
        sync_policy: sync?.sync_policy,
      };
    });
    setConfigByObject(newConfig);
  }, [integrationId, providerSyncConfigurations, activatableObjects, syncConfigurations]);

  // Rehydrate writeback flags
  useEffect(() => {
    if (!providerSyncConfigurations?.length) return;
    const next: WritebackByObject = {};
    providerSyncConfigurations.forEach((s: any) => {
      if (normalizeSyncDirection(s?.sync_direction) === "write_out") {
        const slug = toUiSlug(s.destination_object);
        if (!slug || !activatableObjects.includes(slug as any)) return;
        next[slug] = {
          enabled: s?.status === "enabled",
          allow_creates: s?.allow_creates ?? true,
          allow_updates: s?.allow_updates ?? true,
        };
      }
    });
    if (Object.keys(next).length) {
      setWritebackByObject((prev) => ({ ...prev, ...next }));
    }
  }, [providerSyncConfigurations, activatableObjects]);

  // ─── Initialize default config for enabled objects ──────────────────────

  useEffect(() => {
    const enabledSlugs = mappingConfig.objects.filter((o) => o.enabled).map((o) => o.key);
    if (!enabledSlugs.length) return;

    setConfigByObject((prev) => {
      const next = { ...prev };
      enabledSlugs.forEach((slug) => {
        if (!next[slug]) {
          next[slug] = {
            enabled: true,
            sync_direction: "import_only",
            source_collection: getDefaultSourceCollection(slug),
            matching_method: "external_id",
            matcher_field_name: "external_id",
            hyperengage_matcher_field: "external_id",
            conflict_resolution: "hubspot_wins",
            allow_creates: true,
            allow_updates: true,
            sync_frequency: "daily",
            sync_policy: { update_frequency: "daily" },
          };
        }
      });
      return next;
    });

    setMappingsByObject((prev) => {
      const next = { ...prev };
      enabledSlugs.forEach((slug) => {
        if (!next[slug]) next[slug] = [];
      });
      return next;
    });

    setWritebackByObject((prev) => {
      const next = { ...prev };
      enabledSlugs.forEach((slug) => {
        if (typeof next[slug] === "undefined") {
          next[slug] = { enabled: false, allow_creates: true, allow_updates: true };
        }
      });
      return next;
    });

    setWritebackMappingsByObject((prev) => {
      const next = { ...prev };
      enabledSlugs.forEach((slug) => {
        if (!next[slug]) next[slug] = [];
      });
      return next;
    });

    setRoleMappingsByObject((prev) => {
      const next = { ...prev };
      enabledSlugs.forEach((slug) => {
        if (!next[slug]) next[slug] = [];
      });
      return next;
    });

    setRelationshipMappingsByObject((prev) => {
      const next = { ...prev };
      enabledSlugs.forEach((slug) => {
        if (!next[slug]) next[slug] = [];
      });
      return next;
    });
  }, [mappingConfig.objects, getDefaultSourceCollection]);

  // ─── Cache helpers ──────────────────────────────────────────────────────

  const upsertSyncConfigurationInCache = useCallback(
    (syncConfig: any) => {
      const workspaceId = workspace?.id;
      if (!workspaceId || !syncConfig?.id) return;
      queryClient.setQueryData(["sync-configurations", workspaceId], (oldData: any) => {
        if (!oldData) return [syncConfig];
        if (Array.isArray(oldData)) {
          if (oldData.some((item) => item?.id === syncConfig.id)) return oldData;
          return [...oldData, syncConfig];
        }
        if (oldData?.data && Array.isArray(oldData.data)) {
          if (oldData.data.some((item: any) => item?.id === syncConfig.id)) return oldData;
          return { ...oldData, data: [...oldData.data, syncConfig] };
        }
        return oldData;
      });
    },
    [queryClient, workspace?.id],
  );

  const findExistingInboundSyncId = useCallback((slug: string, sourceCollection?: string): string | undefined => {
    const allSyncConfigurations = extractSyncConfigurations(syncConfigurations as any);
    if (!allSyncConfigurations.length) return undefined;

    const backendSlug = toBackendSlug(slug);
    const candidates = allSyncConfigurations.filter((sync: any) => {
      if (normalizeSyncDirection(sync?.sync_direction) !== "write_in") return false;
      const destinationMatches = sync?.destination_object === backendSlug || toUiSlug(sync?.destination_object) === slug;
      if (!destinationMatches) return false;
      if (providerIntegrationIds.size > 0 && sync?.integration_id && !providerIntegrationIds.has(sync.integration_id)) {
        return false;
      }
      return true;
    });

    if (!candidates.length) return undefined;
    const sorted = [...candidates].sort((a: any, b: any) => {
      const aIntegrationMatch = a?.integration_id === integrationId ? 1 : 0;
      const bIntegrationMatch = b?.integration_id === integrationId ? 1 : 0;
      if (aIntegrationMatch !== bIntegrationMatch) return bIntegrationMatch - aIntegrationMatch;
      const aSourceMatch = sourceCollection && a?.source_object === sourceCollection ? 1 : 0;
      const bSourceMatch = sourceCollection && b?.source_object === sourceCollection ? 1 : 0;
      if (aSourceMatch !== bSourceMatch) return bSourceMatch - aSourceMatch;
      const aUpdated = new Date(a?.updated_at || a?.created_at || 0).getTime();
      const bUpdated = new Date(b?.updated_at || b?.created_at || 0).getTime();
      return bUpdated - aUpdated;
    });

    return sorted[0]?.id;
  }, [syncConfigurations, providerIntegrationIds, integrationId]);

  // ─── Sync management functions ──────────────────────────────────────────

  const ensureInboundSyncInternal = useCallback(
    async (slug: string, sourceCollectionOverride?: string): Promise<string | undefined> => {
      if (!workspace?.id || !integrationId) return undefined;
      if (!workspaceIdBySlug[slug]) return undefined;
      if (inboundSyncIdBySlug[slug]) return inboundSyncIdBySlug[slug];

      const cfg = configByObject[slug];
      const effectiveSource =
        cfg?.source_collection || sourceCollectionOverride || getDefaultSourceCollection(slug);

      setPendingSyncCreations((prev) => new Set([...Array.from(prev), slug]));
      try {
        const data = {
          workspace_id: workspace.id,
          integration_id: integrationId,
          source_object: effectiveSource,
          source_object_id: effectiveSource,
          destination_object: toBackendSlug(slug),
          destination_object_id: workspaceIdBySlug[slug],
          sync_direction: "write_in",
          status: "disabled",
          matcher_field_name: cfg?.matcher_field_name || "external_id",
          source_matcher_field: cfg?.matcher_field_name || "external_id",
          destination_matcher_field: cfg?.hyperengage_matcher_field || "external_id",
          time_window: cfg?.time_window,
          sync_policy: cfg?.sync_policy,
          allow_creates: cfg?.allow_creates ?? true,
          allow_updates: cfg?.allow_updates ?? true,
        } as any;

        let created: any;
        try {
          created = await createSyncConfigMutation.mutateAsync({
            workspace_id: workspace.id,
            data,
          });
        } catch (error: any) {
          const detail = error?.response?.data?.detail;
          const backendErrors = Array.isArray(detail?.errors)
            ? detail.errors
            : Array.isArray(error?.response?.data?.errors)
              ? error.response.data.errors
              : [];
          const duplicateErrorText = [String(error?.message || ""), ...backendErrors.map((entry: any) => String(entry || ""))]
            .join(" ")
            .toLowerCase();

          if (duplicateErrorText.includes("already exists")) {
            const existingSyncId = findExistingInboundSyncId(slug, effectiveSource);
            if (existingSyncId) {
              setLocalInboundSyncIdBySlug((prev) => ({ ...prev, [slug]: existingSyncId }));
              return existingSyncId;
            }
          }

          throw error;
        }
        const createdSync = created?.data ?? created;
        const finalId = createdSync?.id;
        if (finalId) {
          upsertSyncConfigurationInCache(createdSync);
          setLocalInboundSyncIdBySlug((prev) => ({ ...prev, [slug]: finalId }));
          await updateSyncConfigMutation.mutateAsync({
            workspace_id: workspace.id,
            sync_id: finalId,
            data: { status: "disabled" },
          });
        }
        return finalId;
      } finally {
        setPendingSyncCreations((prev) => {
          const s = new Set(prev);
          s.delete(slug);
          return s;
        });
      }
    },
    [
      workspace?.id,
      integrationId,
      workspaceIdBySlug,
      inboundSyncIdBySlug,
      configByObject,
      getDefaultSourceCollection,
      createSyncConfigMutation,
      updateSyncConfigMutation,
      upsertSyncConfigurationInCache,
      findExistingInboundSyncId,
    ],
  );

  const ensureInboundSync = useCallback(
    async (slug: string, sourceCollectionOverride?: string): Promise<string | undefined> => {
      if (!activatableObjects.includes(slug as any)) return undefined;
      const result = await runObjectCommand(
        slug,
        "ensureInboundSync",
        () => ensureInboundSyncInternal(slug, sourceCollectionOverride),
      );
      if (!result.ok && !result.error.includes("already running")) {
        toast.error("Failed to create import sync", {
          description: result.error || "Please review configuration and try again",
        });
      }
      return result.ok ? result.data : undefined;
    },
    [activatableObjects, runObjectCommand, ensureInboundSyncInternal],
  );

  const ensureWritebackSyncInternal = useCallback(
    async (slug: string, inboundSyncId?: string, sourceCollectionOverride?: string): Promise<string | undefined> => {
      if (!workspace?.id || !integrationId) return undefined;
      if (!workspaceIdBySlug[slug]) return undefined;

      const existingId = writebackSyncIdBySlug[slug] || localWritebackSyncIdBySlug[slug];
      if (existingId) return existingId;

      const resolvedInboundSyncId = inboundSyncId || inboundSyncIdBySlug[slug];
      if (!resolvedInboundSyncId) return undefined;

      const sourceCollection = (
        configByObject[slug]?.source_collection
        || sourceCollectionOverride
        || getDefaultSourceCollection(slug)
      );

      const originalSync = {
        id: resolvedInboundSyncId,
        workspace_id: workspace.id,
        integration_id: integrationId,
        source_object: sourceCollection,
        source_object_id: sourceCollection,
        destination_object: toBackendSlug(slug),
        destination_object_id: workspaceIdBySlug[slug],
        sync_direction: "write_in",
        status: "disabled",
        matcher_field_name: configByObject[slug]?.matcher_field_name || "external_id",
        source_matcher_field: configByObject[slug]?.matcher_field_name || "external_id",
        destination_matcher_field: configByObject[slug]?.hyperengage_matcher_field || "external_id",
      } as any;

      const created = await createWriteBackSyncMutation.mutateAsync({
        workspace_id: workspace.id,
        original_sync: originalSync,
        create_missing_records: false,
      });

      const createdId = created?.id;
      if (!createdId) return undefined;

      await updateSyncConfigMutation.mutateAsync({
        workspace_id: workspace.id,
        sync_id: createdId,
        data: { status: "disabled" },
      });
      setLocalWritebackSyncIdBySlug((prev) => ({ ...prev, [slug]: createdId }));
      return createdId;
    },
    [
      workspace?.id,
      integrationId,
      workspaceIdBySlug,
      writebackSyncIdBySlug,
      localWritebackSyncIdBySlug,
      inboundSyncIdBySlug,
      configByObject,
      getDefaultSourceCollection,
      createWriteBackSyncMutation,
      updateSyncConfigMutation,
    ],
  );

  const getFlowKey = useCallback(
    (flowMap: Record<string, string> | undefined, slug: string): string | undefined => {
      if (!flowMap) return undefined;
      const key = flowMap[slug];
      return typeof key === "string" && key.length > 0 ? key : undefined;
    },
    [],
  );

  const nonCoreObjects = useMemo(
    () => activatableObjects.filter((slug) => !syncConfigBackedObjects.has(slug)),
    [activatableObjects, syncConfigBackedObjects],
  );
  const shouldHydrateNonCoreFromFlows = !!isConnected
    && !!connectionId
    && !!integrationId
    && nonCoreObjects.length > 0;

  const nonCoreFlowStatusQuery = useQuery({
    queryKey: [
      "mapping-studio-non-core-flow-status",
      provider.key,
      connectionId,
      integrationId,
      nonCoreObjects.join("|"),
    ],
    enabled: shouldHydrateNonCoreFromFlows,
    staleTime: 60_000,
    queryFn: async () => {
      const result: Record<string, boolean> = {};

      await Promise.all(
        nonCoreObjects.map(async (slug) => {
          const firstFlowKey = getFlowKey(provider.flowKeys.firstSync as Record<string, string>, slug);
          const receiveFlowKey = getFlowKey(provider.flowKeys.receive as Record<string, string>, slug);

          if (!firstFlowKey || !receiveFlowKey) {
            result[slug] = false;
            return;
          }

          const [firstFlowResult, receiveFlowResult] = await Promise.allSettled([
            integrationApp.connection(provider.key).flow(firstFlowKey).get(),
            integrationApp.connection(provider.key).flow(receiveFlowKey).get(),
          ]);

          const firstEnabled = firstFlowResult.status === "fulfilled" && !!firstFlowResult.value?.enabled;
          const receiveEnabled = receiveFlowResult.status === "fulfilled" && !!receiveFlowResult.value?.enabled;

          result[slug] = firstEnabled || receiveEnabled;
        }),
      );

      return result;
    },
  });

  useEffect(() => {
    nonCoreHydratedRef.current = false;
    setNonCoreHydrationApplied(false);
  }, [provider.key, connectionId, integrationId]);

  useEffect(() => {
    if (shouldHydrateNonCoreFromFlows) return;
    nonCoreHydratedRef.current = true;
    setNonCoreHydrationApplied(true);
  }, [shouldHydrateNonCoreFromFlows]);

  useEffect(() => {
    if (!nonCoreFlowStatusQuery.isError) return;
    nonCoreHydratedRef.current = true;
    setNonCoreHydrationApplied(true);
  }, [nonCoreFlowStatusQuery.isError]);

  useEffect(() => {
    if (nonCoreHydratedRef.current) return;
    if (!nonCoreFlowStatusQuery.isSuccess) return;

    nonCoreHydratedRef.current = true;

    const statusBySlug = nonCoreFlowStatusQuery.data || {};
    const enabledSlugs = Object.entries(statusBySlug)
      .filter(([, isEnabled]) => isEnabled)
      .map(([slug]) => slug);
    if (!enabledSlugs.length) {
      setNonCoreHydrationApplied(true);
      return;
    }

    const enabledSet = new Set(enabledSlugs);
    setMappingConfig((prev) => ({
      ...prev,
      objects: prev.objects.map((object) => {
        if (!enabledSet.has(object.key)) return object;
        return {
          ...object,
          enabled: true,
          inboundSyncStatus: "enabled",
        };
      }),
    }));
    setNonCoreHydrationApplied(true);
  }, [nonCoreFlowStatusQuery.isSuccess, nonCoreFlowStatusQuery.data]);

  const handleActivateSync = useCallback(
    async (slug: string) => {
      if (!activatableObjects.includes(slug as any)) return;
      if (!workspace?.id || !integrationId) return;
      const objectConfig = mappingConfig.objects.find((object) => object.key === slug);
      const requiresSyncConfiguration = syncConfigBackedObjects.has(slug);

      const currentMappings = mappingsByObject[slug] || [];
      const validMappings = currentMappings.filter(
        (m) => m.source_field_name && m.destination_field_id && m.mapping_status === "mapped",
      );
      if (requiresSyncConfiguration && objectConfig?.isQuantitative && validMappings.length === 0) {
        toast.error("Field mappings required", {
          description: "Please configure at least one field mapping before activating the sync.",
        });
        return;
      }

      const firstFlowKey = getFlowKey(provider.flowKeys.firstSync as Record<string, string>, slug);
      const receiveFlowKey = getFlowKey(provider.flowKeys.receive as Record<string, string>, slug);
      if (!firstFlowKey || !receiveFlowKey) {
        toast.error("Unsupported object", {
          description: `${slug} cannot be activated for ${provider.label}.`,
        });
        return;
      }

      toast.loading(`Activating ${slug} sync...`, {
        id: `activating-${slug}`,
        description: "Setting up sync configuration and flows",
      });

      const result = await runObjectCommand<string | undefined>(slug, "activateInboundSync", async () => {
        setActivatingSyncs((prev) => new Set([...Array.from(prev), slug]));
        try {
          if (requiresSyncConfiguration) {
            const inboundId = inboundSyncIdBySlug[slug] || (await ensureInboundSyncInternal(slug));
            if (!inboundId) {
              throw new Error("Inbound sync could not be created");
            }

            await updateSyncConfigMutation.mutateAsync({
              workspace_id: workspace.id,
              sync_id: inboundId,
              data: { status: "enabled" },
            });

            await Promise.all([
              integrationApp.connection(provider.key).flow(firstFlowKey).get({ autoCreate: true }),
              integrationApp.connection(provider.key).flow(receiveFlowKey).get({ autoCreate: true }),
            ]);
            await Promise.all([
              integrationApp
                .connection(provider.key)
                .flow(firstFlowKey)
                .patch({ parameters: { sync_id: inboundId }, enabled: true }),
              integrationApp
                .connection(provider.key)
                .flow(receiveFlowKey)
                .patch({ parameters: { sync_id: inboundId }, enabled: true }),
            ]);
            await integrationApp
              .connection(provider.key)
              .flow(firstFlowKey)
              .run({ input: null });

            return inboundId;
          }

          await Promise.all([
            integrationApp.connection(provider.key).flow(firstFlowKey).get({ autoCreate: true }),
            integrationApp.connection(provider.key).flow(receiveFlowKey).get({ autoCreate: true }),
          ]);

          const flowObjectType = slug === "emails" ? "emails" : slug;
          await Promise.all([
            integrationApp
              .connection(provider.key)
              .flow(firstFlowKey)
              .patch({ parameters: { object_type: flowObjectType, batch: 0 }, enabled: true }),
            integrationApp
              .connection(provider.key)
              .flow(receiveFlowKey)
              .patch({ parameters: { object_type: flowObjectType }, enabled: true }),
          ]);
          await integrationApp
            .connection(provider.key)
            .flow(firstFlowKey)
            .run({ input: null });
          return undefined;
        } finally {
          setActivatingSyncs((prev) => {
            const s = new Set(prev);
            s.delete(slug);
            return s;
          });
        }
      });

      if (!result.ok) {
        if (result.error.includes("already running")) return;
        toast.error("Activation failed", {
          id: `activating-${slug}`,
          description: result.error || "Could not activate the sync",
        });
        return;
      }

      toast.success(`${provider.label} sync activated`, {
        id: `activating-${slug}`,
        description: "Inbound sync enabled and initial backfill started.",
      });
    },
    [
      activatableObjects,
      workspace?.id,
      integrationId,
      mappingsByObject,
      getFlowKey,
      provider.flowKeys.firstSync,
      provider.flowKeys.receive,
      provider.label,
      provider.key,
      syncConfigBackedObjects,
      runObjectCommand,
      mappingConfig.objects,
      inboundSyncIdBySlug,
      ensureInboundSyncInternal,
      updateSyncConfigMutation,
      integrationApp,
    ],
  );

  const handleDeactivateSync = useCallback(
    async (slug: string) => {
      if (!activatableObjects.includes(slug as any)) return;
      if (!workspace?.id || !integrationId) return;

      const firstFlowKey = getFlowKey(provider.flowKeys.firstSync as Record<string, string>, slug);
      const receiveFlowKey = getFlowKey(provider.flowKeys.receive as Record<string, string>, slug);
      if (!firstFlowKey || !receiveFlowKey) return;

      const result = await runObjectCommand(slug, "deactivateInboundSync", async () => {
        setActivatingSyncs((prev) => new Set([...Array.from(prev), slug]));
        try {
          if (syncConfigBackedObjects.has(slug)) {
            const inboundId = inboundSyncIdBySlug[slug];
            if (inboundId) {
              await updateSyncConfigMutation.mutateAsync({
                workspace_id: workspace.id,
                sync_id: inboundId,
                data: { status: "disabled" },
              });
            }
          }
          await Promise.all([
            integrationApp.connection(provider.key).flow(firstFlowKey).patch({ enabled: false }),
            integrationApp.connection(provider.key).flow(receiveFlowKey).patch({ enabled: false }),
          ]);
        } finally {
          setActivatingSyncs((prev) => {
            const s = new Set(prev);
            s.delete(slug);
            return s;
          });
        }
      });

      if (!result.ok) {
        if (result.error.includes("already running")) return;
        toast.error("Sync disable failed", {
          description: result.error || `Could not disable ${slug} sync`,
        });
        return;
      }

      toast.warning("Sync disabled", {
        description: `Disabled ${slug} inbound sync`,
      });
    },
    [
      activatableObjects,
      workspace?.id,
      integrationId,
      getFlowKey,
      provider.flowKeys.firstSync,
      provider.flowKeys.receive,
      syncConfigBackedObjects,
      inboundSyncIdBySlug,
      runObjectCommand,
      updateSyncConfigMutation,
      integrationApp,
      provider.key,
    ],
  );

  // ─── Write-back management ──────────────────────────────────────────────

  const handleToggleWriteback = useCallback(
    async (slug: string, enabled: boolean) => {
      if (!activatableObjects.includes(slug as any)) return;

      setWritebackByObject((prev) => ({
        ...prev,
        [slug]: {
          enabled,
          allow_creates: prev[slug]?.allow_creates ?? true,
          allow_updates: prev[slug]?.allow_updates ?? true,
        },
      }));

      if (!workspace?.id || !integrationId) return;

      const result = await runObjectCommand(slug, "toggleWriteback", async () => {
        const inboundId = inboundSyncIdBySlug[slug] || (await ensureInboundSyncInternal(slug));
        if (!inboundId) return;

        const wbId = writebackSyncIdBySlug[slug] || localWritebackSyncIdBySlug[slug];
        if (enabled && !wbId) {
          const sourceCollection = configByObject[slug]?.source_collection || getDefaultSourceCollection(slug);
          const originalSync = {
            id: inboundId,
            workspace_id: workspace.id,
            integration_id: integrationId,
            source_object: sourceCollection,
            source_object_id: sourceCollection,
            destination_object: toBackendSlug(slug),
            destination_object_id: workspaceIdBySlug[slug],
            sync_direction: "write_in",
            status: "disabled",
            matcher_field_name: configByObject[slug]?.matcher_field_name,
          } as any;
          const created = await createWriteBackSyncMutation.mutateAsync({
            workspace_id: workspace.id,
            original_sync: originalSync,
            create_missing_records: false,
          });
          const finalId = created?.id;
          if (finalId) {
            await updateSyncConfigMutation.mutateAsync({
              workspace_id: workspace.id,
              sync_id: finalId,
              data: { status: "disabled" },
            });
            setLocalWritebackSyncIdBySlug((prev) => ({ ...prev, [slug]: finalId }));
          }
          return;
        }

        if (!enabled && wbId) {
          await updateSyncConfigMutation.mutateAsync({
            workspace_id: workspace.id,
            sync_id: wbId,
            data: { status: "disabled" },
          });
        }
      });

      if (!result.ok && !result.error.includes("already running")) {
        toast.error("Write-back update failed", {
          description: result.error || `Failed to update write-back for ${slug}`,
        });
      }
    },
    [
      activatableObjects,
      workspace?.id,
      integrationId,
      runObjectCommand,
      inboundSyncIdBySlug,
      ensureInboundSyncInternal,
      writebackSyncIdBySlug,
      localWritebackSyncIdBySlug,
      configByObject,
      getDefaultSourceCollection,
      workspaceIdBySlug,
      createWriteBackSyncMutation,
      updateSyncConfigMutation,
    ],
  );

  const handleActivateWritebackSync = useCallback(
    async (slug: string) => {
      if (!activatableObjects.includes(slug as any)) return;
      if (!workspace?.id || !integrationId) return;

      const sendFlowKey = getFlowKey(provider.flowKeys.send as Record<string, string>, slug);
      if (!sendFlowKey) {
        toast.error("Unsupported object", {
          description: `${slug} write-back is not available for ${provider.label}.`,
        });
        return;
      }

      const result = await runObjectCommand(slug, "activateWritebackSync", async () => {
        const wbId = writebackSyncIdBySlug[slug] || localWritebackSyncIdBySlug[slug];
        if (wbId) {
          await updateSyncConfigMutation.mutateAsync({
            workspace_id: workspace.id,
            sync_id: wbId,
            data: { status: "enabled" },
          });
        }

        await integrationApp.connection(provider.key).flow(sendFlowKey).get({ autoCreate: true });
        await integrationApp.connection(provider.key).flow(sendFlowKey).patch({ enabled: true });
      });

      if (!result.ok) {
        if (result.error.includes("already running")) return;
        toast.error("Error enabling write-back", {
          description: result.error || `Failed to enable write-back sync for ${slug}`,
        });
        return;
      }

      toast.success("Write-back enabled", {
        description: `Hyperengage → ${provider.label}`,
      });
    },
    [
      activatableObjects,
      workspace?.id,
      integrationId,
      getFlowKey,
      provider.flowKeys.send,
      provider.label,
      provider.key,
      runObjectCommand,
      writebackSyncIdBySlug,
      localWritebackSyncIdBySlug,
      updateSyncConfigMutation,
      integrationApp,
    ],
  );

  const handleDeactivateWritebackSync = useCallback(
    async (slug: string) => {
      if (!activatableObjects.includes(slug as any)) return;
      if (!workspace?.id || !integrationId) return;

      const sendFlowKey = getFlowKey(provider.flowKeys.send as Record<string, string>, slug);
      if (!sendFlowKey) return;

      const result = await runObjectCommand(slug, "deactivateWritebackSync", async () => {
        const wbId = writebackSyncIdBySlug[slug] || localWritebackSyncIdBySlug[slug];
        if (wbId) {
          await updateSyncConfigMutation.mutateAsync({
            workspace_id: workspace.id,
            sync_id: wbId,
            data: { status: "disabled" },
          });
        }
        await integrationApp.connection(provider.key).flow(sendFlowKey).patch({ enabled: false });
      });

      if (!result.ok) {
        if (result.error.includes("already running")) return;
        toast.error("Write-back disable failed", {
          description: result.error || `Failed to disable write-back for ${slug}`,
        });
        return;
      }

      toast.warning("Write-back disabled", {
        description: `Disabled write-back sync for ${slug}`,
      });
    },
    [
      activatableObjects,
      workspace?.id,
      integrationId,
      getFlowKey,
      provider.flowKeys.send,
      provider.key,
      runObjectCommand,
      writebackSyncIdBySlug,
      localWritebackSyncIdBySlug,
      updateSyncConfigMutation,
      integrationApp,
    ],
  );

  // ─── Provider fields management ─────────────────────────────────────────

  const fetchCollectionsForObject = useCallback(
    async (slug: string) => {
      if (!isConnected) return;
      try {
        const collections = await integrationApp
          .integration(providerKey)
          .getDataCollections();
        const mapped = collections.map((col: any) => ({
          key: col.key,
          name: col.name,
          fields: [],
        }));
        setProviderCollectionsByObject((prev) => ({ ...prev, [slug]: mapped }));
      } catch (error) {
        console.error(`Failed to fetch collections for ${slug}:`, error);
      }
    },
    [isConnected, integrationApp, providerKey],
  );

  const fetchFieldsForCollection = useCallback(
    async (slug: string, collectionKey: string) => {
      if (!isConnected || !connectionId) return;
      setProviderFieldsLoadingByObject((prev) => ({ ...prev, [slug]: true }));
      try {
        const collection = await integrationApp
          .connection(connectionId)
          .dataCollection(collectionKey)
          .get();
        if (collection?.fieldsSchema?.properties) {
          const fields: HubSpotField[] = Object.entries(
            collection.fieldsSchema.properties,
          ).map(([key, field]: [string, any]) => ({
            ...field,
            key,
            title: field.title || field.displayName || key,
            type: field.type || "string",
            readOnly: field.readOnly || false,
            required: field.required || false,
          }));
          setProviderFieldsByObject((prev) => ({ ...prev, [slug]: fields }));
          setProviderCollectionsByObject((prev) => ({
            ...prev,
            [slug]: (prev[slug] || []).map((col) =>
              col.key === collectionKey ? { ...col, fields } : col,
            ),
          }));
        }
      } catch (error) {
        console.error(`Failed to fetch fields for ${collectionKey}:`, error);
      } finally {
        setProviderFieldsLoadingByObject((prev) => ({ ...prev, [slug]: false }));
      }
    },
    [isConnected, connectionId, integrationApp],
  );

  const handleCollectionChange = useCallback(
    async (slug: string, collectionKey: string | null) => {
      if (!collectionKey) return;
      setSelectedCollectionByObject((prev) => ({ ...prev, [slug]: collectionKey }));

      // Update data source in integration.app
      if (connectionId) {
        try {
          await integrationApp
            .connection(connectionId)
            .dataSource(slug)
            .patch({ collectionKey, collectionParameters: {} });
        } catch (error) {
          console.error("Failed to update collection:", error);
        }
      }

      // Update config
      setConfigByObject((prev) => ({
        ...prev,
        [slug]: { ...prev[slug], source_collection: collectionKey },
      }));

      // Fetch fields for new collection
      await fetchFieldsForCollection(slug, collectionKey);
    },
    [connectionId, integrationApp, fetchFieldsForCollection],
  );

  // Initialize provider fields for enabled objects
  useEffect(() => {
    const enabledObjects = mappingConfig.objects.filter((o) => o.enabled && o.isQuantitative);
    enabledObjects.forEach(async (obj) => {
      if (providerCollectionsByObject[obj.key]?.length) return;
      await fetchCollectionsForObject(obj.key);

      // Get current data source collection
      if (connectionId) {
        try {
          const dataSource = await integrationApp
            .connection(connectionId)
            .dataSource(obj.key)
            .get();
          const collKey = dataSource?.collectionKey;
          if (collKey) {
            setSelectedCollectionByObject((prev) => ({ ...prev, [obj.key]: collKey }));
            await fetchFieldsForCollection(obj.key, collKey);
          }
        } catch {
          // Data source might not exist yet
        }
      }
    });
  }, [
    mappingConfig.objects,
    isConnected,
    connectionId,
    fetchCollectionsForObject,
    fetchFieldsForCollection,
    integrationApp,
    providerCollectionsByObject,
  ]);

  // ─── Stable callbacks ─────────────────────────────────────────────────

  const onConfigPatchCb = useCallback(
    (slug: string, patch: Partial<ConfigByObject[string]>) => {
      setConfigByObject((prev) => ({
        ...prev,
        [slug]: {
          ...prev[slug],
          ...patch,
          source_collection:
            patch.source_collection || prev[slug]?.source_collection || getDefaultSourceCollection(slug),
        },
      }));
      // When source_collection changes, update mappings source fields
      if (patch.source_collection) {
        setMappingsByObject((prev) => ({
          ...prev,
          [slug]: (prev[slug] || []).map((m) => ({
            ...m,
            source_field_name: m.source_field_name,
            source_field_id: m.source_field_id,
          })),
        }));
      }
    },
    [getDefaultSourceCollection],
  );

  const onMappingsChangeCb = useCallback(
    (updater: (prev: DraftMappingsByObject) => DraftMappingsByObject) => {
      setMappingsByObject((prev) => updater(prev));
    },
    [],
  );

  const onRoleMappingsChangeCb = useCallback(
    (updater: (prev: RoleMappingsByObject) => RoleMappingsByObject) => {
      setRoleMappingsByObject((prev) => updater(prev));
    },
    [],
  );

  const onRelationshipMappingsChangeCb = useCallback(
    (updater: (prev: RelationshipMappingsByObject) => RelationshipMappingsByObject) => {
      setRelationshipMappingsByObject((prev) => updater(prev));
    },
    [],
  );

  const onWritebackChangeCb = useCallback(
    (updater: (prev: WritebackByObject) => WritebackByObject) => {
      setWritebackByObject((prev) => updater(prev));
    },
    [],
  );

  const buildObjectHash = useCallback((
    slug: string,
    overrides?: {
      writebackMappings?: WritebackFieldMapping[];
    },
  ) => {
    const objectConfig = mappingConfig.objects.find((obj) => obj.key === slug);
    return JSON.stringify({
      enabled: !!objectConfig?.enabled,
      syncMode: objectConfig?.syncMode || null,
      hasExternalId: objectConfig?.hasExternalId ?? null,
      externalIdField: objectConfig?.externalIdField || null,
      config: configByObject[slug] || null,
      mappings: mappingsByObject[slug] || [],
      roleMappings: roleMappingsByObject[slug] || [],
      relationshipMappings: relationshipMappingsByObject[slug] || [],
      writeback: writebackByObject[slug] || null,
      writebackMappings: overrides?.writebackMappings ?? (writebackMappingsByObject[slug] || []),
    });
  }, [
    mappingConfig.objects,
    configByObject,
    mappingsByObject,
    roleMappingsByObject,
    relationshipMappingsByObject,
    writebackByObject,
    writebackMappingsByObject,
  ]);

  const onWritebackMappingsChangeCb = useCallback((
    slug: string,
    mappings: WritebackFieldMapping[],
    source: "hydrate" | "user" = "user",
  ) => {
    setWritebackMappingsByObject((prev) => ({ ...prev, [slug]: mappings }));

    // Hydration should establish baseline snapshot, not mark object dirty.
    if (source === "hydrate" && snapshotInitializedRef.current) {
      setPersistedObjectHashes((prev) => ({
        ...prev,
        [slug]: buildObjectHash(slug, { writebackMappings: mappings }),
      }));
    }
  }, [buildObjectHash]);

  const objectHashBySlug = useMemo(() => {
    const hashes: Record<string, string> = {};
    activatableObjects.forEach((slug) => {
      hashes[slug] = buildObjectHash(slug);
    });
    return hashes;
  }, [activatableObjects, buildObjectHash]);

  useEffect(() => {
    if (snapshotInitializedRef.current) return;
    if (activatableObjects.length === 0) return;
    if (!integrationId) return;
    if (typeof syncConfigurations === "undefined") return;
    if (!coreHydrationApplied) return;
    if (nonCoreObjects.length > 0 && !nonCoreHydrationApplied) return;
    setPersistedObjectHashes(objectHashBySlug);
    snapshotInitializedRef.current = true;
  }, [
    activatableObjects,
    integrationId,
    syncConfigurations,
    coreHydrationApplied,
    objectHashBySlug,
    nonCoreObjects.length,
    nonCoreHydrationApplied,
  ]);

  const reservationStateBySlug = useMemo(() => {
    const next: Record<string, ReturnType<typeof buildObjectReservations>> = {};
    activatableObjects.forEach((slug) => {
      next[slug] = buildObjectReservations({
        matcherFieldName: configByObject[slug]?.matcher_field_name,
        mappings: mappingsByObject[slug] || [],
        roleMappings: roleMappingsByObject[slug] || [],
        relationshipMappings: relationshipMappingsByObject[slug] || [],
        writebackMappings: writebackByObject[slug]?.enabled ? (writebackMappingsByObject[slug] || []) : [],
      });
    });
    return next;
  }, [
    activatableObjects,
    configByObject,
    mappingsByObject,
    roleMappingsByObject,
    relationshipMappingsByObject,
    writebackMappingsByObject,
  ]);

  const enabledObjects = useMemo(
    () => mappingConfig.objects
      .filter((object) => object.enabled && activatableObjects.includes(object.key as any))
      .map((object) => object.key),
    [mappingConfig.objects, activatableObjects],
  );

  const dirtyObjects = useMemo(
    () => activatableObjects.filter((slug) => persistedObjectHashes[slug] !== objectHashBySlug[slug]),
    [activatableObjects, persistedObjectHashes, objectHashBySlug],
  );

  const hasDraftChanges = dirtyObjects.length > 0;
  const hasConflicts = useMemo(
    () => enabledObjects.some((slug) => (reservationStateBySlug[slug]?.conflicts.length || 0) > 0),
    [enabledObjects, reservationStateBySlug],
  );

  const getWorkspaceFieldsForSlug = useCallback(async (slug: string): Promise<FieldDefinition[]> => {
    if (!workspace?.id) return [];
    const apiSlug = slug === "contacts"
      ? "users"
      : slug === "emails"
        ? "conversation_messages"
        : slug;
    const queryKey = ["sync-object-fields", workspace.id, apiSlug];
    const cached = queryClient.getQueryData(queryKey) as any;
    const cachedFields = Array.isArray(cached?.data)
      ? cached.data
      : Array.isArray(cached)
        ? cached
        : [];
    if (cachedFields.length > 0) {
      return cachedFields;
    }

    const response = await getSyncObjectFieldsApi({
      workspace_id: workspace.id,
      object_slug: apiSlug,
    });

    const fields = Array.isArray((response as any)?.data)
      ? (response as any).data
      : Array.isArray(response)
        ? response
        : [];
    return fields;
  }, [workspace?.id, queryClient]);

  const handleSaveConfiguration = useCallback(async (options?: { showSuccessToast?: boolean; targetObjectKey?: string }): Promise<boolean> => {
    const showSuccessToast = options?.showSuccessToast ?? true;
    const targetObjectKey = options?.targetObjectKey;
    const isTargetedSave = typeof targetObjectKey === "string" && targetObjectKey.length > 0;
    if (!workspace?.id || !integrationId || !connectionId) return false;
    const objectsToSave = isTargetedSave
      ? enabledObjects.filter((slug) => slug === targetObjectKey)
      : enabledObjects;

    if (!objectsToSave.length) {
      const targetObjectLabel = mappingConfig.objects.find((object) => object.key === targetObjectKey)?.label || targetObjectKey;
      toast.warning(isTargetedSave ? `${targetObjectLabel} not enabled` : "No objects selected", {
        description: isTargetedSave
          ? `Enable ${targetObjectLabel} before syncing.`
          : "Enable at least one object before saving configuration.",
      });
      return false;
    }

    const validationErrors = objectsToSave.flatMap((slug) => {
      if (!syncConfigBackedObjects.has(slug)) {
        return [];
      }
      const objectConfig = mappingConfig.objects.find((object) => object.key === slug);
      return validateObjectDraft({
        object: slug,
        enabled: !!objectConfig?.enabled,
        matcherFieldName: configByObject[slug]?.matcher_field_name || "external_id",
        conflicts: reservationStateBySlug[slug]?.conflicts || [],
        enabledObjects,
        relationshipMappings: relationshipMappingsByObject[slug] || [],
        writebackEnabled: !!writebackByObject[slug]?.enabled,
        writebackMappings: writebackMappingsByObject[slug] || [],
      });
    });

    if (validationErrors.length > 0) {
      toast.error("Validation failed", {
        description: validationErrors[0]?.message || "Please resolve validation errors before saving.",
      });
      return false;
    }

    setSavingConfiguration(true);
    const saveOrder = ["accounts", "contacts", "deals", "notes", "tasks", "emails", "companies"] as const;
    const failedObjects: string[] = [];
    const savedObjects: string[] = [];

    try {
      const saveTargetSet = new Set(objectsToSave);
      for (const slug of saveOrder) {
        if (!saveTargetSet.has(slug)) continue;
        if (!syncConfigBackedObjects.has(slug)) {
          setPersistedObjectHashes((prev) => ({
            ...prev,
            [slug]: objectHashBySlug[slug],
          }));
          savedObjects.push(slug);
          continue;
        }

        const objectConfig = mappingConfig.objects.find((object) => object.key === slug);
        const workspaceObjectId = workspaceIdBySlug[slug];
        if (!objectConfig || !workspaceObjectId) {
          failedObjects.push(slug);
          continue;
        }

        const saveResult = await runObjectCommand(slug, "saveConfiguration", async () => {
          const sourceCollection = configByObject[slug]?.source_collection || getDefaultSourceCollection(slug);

          if (!objectConfig.isQuantitative) {
            const ensuredInboundId = inboundSyncIdBySlug[slug]
              || await ensureInboundSyncInternal(slug, sourceCollection);
            if (!ensuredInboundId) {
              throw new Error(`Could not ensure inbound sync for ${slug}`);
            }
            await updateSyncConfigMutation.mutateAsync({
              workspace_id: workspace.id,
              sync_id: ensuredInboundId,
              data: {
                source_object: sourceCollection,
                source_object_id: sourceCollection,
                destination_object: toBackendSlug(slug),
                destination_object_id: workspaceObjectId,
                sync_direction: "write_in",
                status: "disabled",
                matcher_field_name: configByObject[slug]?.matcher_field_name || "external_id",
                source_matcher_field: configByObject[slug]?.matcher_field_name || "external_id",
                destination_matcher_field: configByObject[slug]?.hyperengage_matcher_field || "external_id",
                allow_creates: configByObject[slug]?.allow_creates ?? true,
                allow_updates: configByObject[slug]?.allow_updates ?? true,
                sync_policy: configByObject[slug]?.sync_policy,
                time_window: configByObject[slug]?.time_window,
              } as any,
            });
            return { inboundSyncId: ensuredInboundId } as any;
          }

          const workspaceFields = await getWorkspaceFieldsForSlug(slug);
          if (!workspaceFields.length) {
            throw new Error(`Workspace fields unavailable for ${slug}`);
          }

          const commitResult = await commitObject({
            object: slug as "accounts" | "companies" | "contacts" | "deals",
            workspaceObjectId,
            providerKey: provider.key,
            sourceCollection,
            config: configByObject[slug],
            draftMappings: mappingsByObject[slug] || [],
            roleMappings: roleMappingsByObject[slug] || [],
            relationshipMappings: relationshipMappingsByObject[slug] || [],
            writebackConfig: writebackByObject[slug] || {
              enabled: false,
              allow_creates: false,
              allow_updates: true,
            },
            writebackMappings: writebackMappingsByObject[slug] || [],
            workspaceFields,
            providerFields: providerFieldsByObject[slug] || [],
            systemMappingFields: provider.systemMappingFields || [],
            inboundSyncId: inboundSyncIdBySlug[slug],
            writebackSyncId: writebackSyncIdBySlug[slug],
            dependencies: {
              ensureInboundSync: (targetSlug, sourceCollection) => (
                ensureInboundSyncInternal(targetSlug, sourceCollection)
              ),
              ensureWritebackSync: (targetSlug, inboundId, sourceCollection) => (
                ensureWritebackSyncInternal(targetSlug, inboundId, sourceCollection)
              ),
              updateSyncConfiguration: async (syncId, data) => {
                await updateSyncConfigMutation.mutateAsync({
                  workspace_id: workspace.id,
                  sync_id: syncId,
                  data,
                });
              },
              getFieldMappings: async (syncId) => {
                const mappings = await getFieldMappings({ workspace_id: workspace.id, sync_id: syncId });
                if (Array.isArray((mappings as any)?.data)) {
                  return (mappings as any).data;
                }
                return Array.isArray(mappings) ? mappings : [];
              },
              updateFieldMapping: async (syncId, mappingId, data) => {
                await updateFieldMapping({
                  workspace_id: workspace.id,
                  sync_id: syncId,
                  mapping_id: mappingId,
                  data,
                });
              },
              deleteFieldMapping: async (syncId, mappingId) => {
                await deleteFieldMapping({
                  workspace_id: workspace.id,
                  sync_id: syncId,
                  mapping_id: mappingId,
                });
              },
              createBulkFieldMappings: async (syncId, mappings) => {
                await createBulkFieldMappings({
                  workspace_id: workspace.id,
                  sync_id: syncId,
                  mappings: mappings as any,
                });
              },
              patchIntegrationFieldMapping: async (fieldMappingKey, payload) => {
                const fieldMappingAccessor = integrationApp
                  .connection(connectionId)
                  .fieldMapping(fieldMappingKey);

                await fieldMappingAccessor.get({ autoCreate: true });
                await fieldMappingAccessor.patch(payload);
              },
            },
          });

          return commitResult;
        });

        if (!saveResult.ok) {
          failedObjects.push(slug);
          continue;
        }

        const committed = saveResult.data;
        if (committed?.inboundSyncId) {
          setLocalInboundSyncIdBySlug((prev) => ({
            ...prev,
            [slug]: committed.inboundSyncId,
          }));
        }
        if (committed?.writebackSyncId) {
          setLocalWritebackSyncIdBySlug((prev) => ({
            ...prev,
            [slug]: committed.writebackSyncId as string,
          }));
        }

        setPersistedObjectHashes((prev) => ({
          ...prev,
          [slug]: objectHashBySlug[slug],
        }));
        savedObjects.push(slug);
      }

      if (failedObjects.length > 0) {
        toast.warning("Configuration partially saved", {
          description: `Saved: ${savedObjects.join(", ") || "none"} | Failed: ${failedObjects.join(", ")}`,
        });
        return false;
      }

      if (showSuccessToast) {
        const targetObjectLabel = mappingConfig.objects.find((object) => object.key === targetObjectKey)?.label || targetObjectKey;
        toast.success("Configuration saved", {
          description: isTargetedSave
            ? `${targetObjectLabel} configuration saved successfully.`
            : "All enabled objects were saved successfully. You can now start sync.",
        });
      }
      return true;
    } finally {
      setSavingConfiguration(false);
    }
  }, [
    workspace?.id,
    integrationId,
    connectionId,
    enabledObjects,
    mappingConfig.objects,
    configByObject,
    syncConfigBackedObjects,
    reservationStateBySlug,
    relationshipMappingsByObject,
    writebackByObject,
    writebackMappingsByObject,
    workspaceIdBySlug,
    runObjectCommand,
    getWorkspaceFieldsForSlug,
    provider.key,
    provider.systemMappingFields,
    providerFieldsByObject,
    inboundSyncIdBySlug,
    writebackSyncIdBySlug,
    ensureInboundSyncInternal,
    ensureWritebackSyncInternal,
    updateSyncConfigMutation,
    workspace?.id,
    integrationApp,
    objectHashBySlug,
    getDefaultSourceCollection,
  ]);

  // ─── Object update handler ─────────────────────────────────────────────

  const handleUpdateObject = useCallback(
    (objectKey: string, updates: Partial<ObjectConfig>) => {
      if (updates.enabled === true && !activatableObjects.includes(objectKey as any)) {
        return;
      }
      setMappingConfig((prev) => ({
        ...prev,
        objects: prev.objects.map((o) =>
          o.key === objectKey ? { ...o, ...updates } : o,
        ),
      }));
    },
    [activatableObjects],
  );

  // ─── Sync CTA handler ──────────────────────────────────────────────────

  const handleSync = useCallback(async (targetObjectKey?: string) => {
    if (savingConfiguration) return;
    const syncObjectKey = targetObjectKey || activeTab;
    const targetObject = mappingConfig.objects.find((object) => object.key === syncObjectKey);
    if (!targetObject || !activatableObjects.includes(syncObjectKey as any)) {
      toast.error("Unsupported object", {
        description: "Select a supported object to sync.",
      });
      return;
    }
    if (!targetObject.enabled) {
      toast.warning(`${targetObject.label} not enabled`, {
        description: `Enable ${targetObject.label} before starting sync.`,
      });
      return;
    }

    const hasTargetConflicts = (reservationStateBySlug[syncObjectKey]?.conflicts.length || 0) > 0;
    if (hasTargetConflicts) {
      toast.error("Resolve duplicate field usage", {
        description: `Remove duplicate provider field selections in ${targetObject.label} before starting sync.`,
      });
      return;
    }

    const saved = await handleSaveConfiguration({
      showSuccessToast: !singleSyncCta,
      targetObjectKey: syncObjectKey,
    });
    if (!saved) return;

    setMappingConfig((prev) => ({ ...prev, syncing: true }));

    try {
      try {
        await handleActivateSync(syncObjectKey);
      } catch (error) {
        console.error(`Failed to activate sync for ${syncObjectKey}:`, error);
      }

      if (writebackByObject[syncObjectKey]?.enabled) {
        if (!writebackSyncIdBySlug[syncObjectKey] && !localWritebackSyncIdBySlug[syncObjectKey]) {
          toast.warning(`Write-back not saved for ${targetObject.label}`, {
            description: "Save configuration first to create write-back sync.",
          });
        } else {
          try {
            await handleActivateWritebackSync(syncObjectKey);
          } catch (error) {
            console.error(`Failed to activate writeback for ${syncObjectKey}:`, error);
          }
        }
      } else if (writebackSyncIdBySlug[syncObjectKey] || localWritebackSyncIdBySlug[syncObjectKey]) {
        try {
          await handleDeactivateWritebackSync(syncObjectKey);
        } catch (error) {
          console.error(`Failed to deactivate writeback for ${syncObjectKey}:`, error);
        }
      }

      toast.success("Sync started", {
        description: `${targetObject.label} is now syncing with Hyperengage.`,
      });
    } finally {
      setMappingConfig((prev) => ({ ...prev, syncing: false }));
    }
  }, [
    savingConfiguration,
    activeTab,
    handleSaveConfiguration,
    mappingConfig.objects,
    activatableObjects,
    handleActivateSync,
    handleActivateWritebackSync,
    handleDeactivateWritebackSync,
    reservationStateBySlug,
    writebackByObject,
    writebackSyncIdBySlug,
    localWritebackSyncIdBySlug,
    provider.label,
    singleSyncCta,
  ]);

  // ─── Derived values ─────────────────────────────────────────────────────

  const activeObject = mappingConfig.objects.find((o) => o.key === activeTab);
  const hasAnyEnabled = mappingConfig.objects.some(
    (o) => o.enabled && activatableObjects.includes(o.key as any),
  );
  const activeObjectIndex = mappingConfig.objects.findIndex((object) => object.key === activeTab);
  const nextObject = activeObjectIndex >= 0 ? mappingConfig.objects[activeObjectIndex + 1] : undefined;
  const canGoToNextObject = !!nextObject;
  const nextObjectCtaLabel = useMemo(() => {
    if (!nextObject) return "";
    if (nextObject.key === "contacts") return "Configure user/contact sync";
    return `Configure ${nextObject.label.toLowerCase()} sync`;
  }, [nextObject]);
  const canSyncActiveObject = !!activeObject
    && activeObject.enabled
    && activatableObjects.includes(activeObject.key as any);
  const activeObjectHasConflicts = !!activeObject
    && ((reservationStateBySlug[activeObject.key]?.conflicts.length || 0) > 0);
  const relationshipHydrationPending = !coreHydrationApplied || !nonCoreHydrationApplied;

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="space-y-0">
      {/* Header */}
      <div className="mb-6">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1 text-xs text-[var(--mantine-color-dimmed)] hover:text-[var(--mantine-color-text)] transition-colors mb-4"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to {provider.label}
        </button>

        <div className="flex items-center gap-3">
          <CompanyLogo
            domain={getDomainFromCompany(provider.label)}
            name={provider.label}
            className="w-8 h-8"
          />
          <div>
            <Text size="md" fw={600}>
              {provider.label} Configuration
            </Text>
          </div>
        </div>
      </div>

      {/* Status banner */}
      {!mappingConfig.syncing && (
        <div className="flex items-start gap-2.5 rounded-lg border border-solid border-[var(--mantine-color-default-border)] bg-[var(--mantine-color-gray-0)] px-4 py-3 mb-6">
          <Info className="h-4 w-4 text-[var(--mantine-color-dimmed)] mt-0.5 flex-shrink-0" />
          <div>
            <Text size="sm" fw={500}>
              Your {provider.label} data is not yet syncing with Hyperengage.
            </Text>
            <Text size="xs" c="dimmed" mt={2}>
              Once you complete the form below, we'll import and sync your {provider.label} data
              into Hyperengage.
            </Text>
          </div>
        </div>
      )}

      {/* Relationship diagram */}
      {relationshipHydrationPending ? (
        <div className="border border-border rounded-lg p-6 text-center">
          <p className="text-[13px] text-muted-foreground">Loading object relationships...</p>
        </div>
      ) : (
        <RelationshipDiagram
          objects={mappingConfig.objects}
          integrationName={provider.label}
        />
      )}

      {/* Object tabs */}
      <div className="border-b border-solid border-[var(--mantine-color-default-border)] mb-6 mt-6">
        <div className="flex items-center gap-0 -mb-px overflow-x-auto">
          {mappingConfig.objects.map((obj) => (
            <button
              key={obj.key}
              type="button"
              onClick={() => setActiveTab(obj.key)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm whitespace-nowrap border-b-2 border-solid transition-colors ${
                activeTab === obj.key
                  ? "text-[var(--mantine-color-text)] font-medium border-[var(--mantine-primary-color-filled)]"
                  : "text-[var(--mantine-color-dimmed)] border-transparent hover:text-[var(--mantine-color-text)] hover:border-[var(--mantine-color-default-border)]"
              }`}
            >
              {obj.isQuantitative && <Settings2 className="h-3 w-3" />}
              {obj.label}
              {obj.enabled && (
                <div className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Active tab content */}
      {activeObject &&
        (activeObject.isQuantitative ? (
          <ObjectTab
            object={activeObject}
            integrationName={provider.label}
            providerKey={providerKey}
            integrationId={integrationId}
            connectionId={connectionId}
            isConnected={isConnected}
            onUpdate={(updates) => handleUpdateObject(activeObject.key, updates)}
            syncId={inboundSyncIdBySlug[activeObject.key]}
            writebackSyncId={writebackSyncIdBySlug[activeObject.key]}
            workspaceObjectId={workspaceIdBySlug[activeObject.key]}
            draftMappings={mappingsByObject}
            writeback={writebackByObject}
            roleMappingsState={roleMappingsByObject}
            relationshipMappingsState={relationshipMappingsByObject}
            config={configByObject}
            onConfigPatch={onConfigPatchCb}
            onMappingsChange={onMappingsChangeCb}
            onRoleMappingsChange={onRoleMappingsChangeCb}
            onRelationshipMappingsChange={onRelationshipMappingsChangeCb}
            onWritebackChange={onWritebackChangeCb}
            writebackMappingsState={writebackMappingsByObject}
            onWritebackMappingsChange={onWritebackMappingsChangeCb}
            onToggleWriteback={handleToggleWriteback}
            onCreateSync={ensureInboundSync}
            onActivateSync={handleActivateSync}
            onDeactivateSync={handleDeactivateSync}
            persistenceMode="managed"
            reservedProviderFields={reservationStateBySlug[activeObject.key]?.reservedFields || new Set<string>()}
            fieldConflicts={(reservationStateBySlug[activeObject.key]?.conflicts || []).map((conflict) => conflict.fieldKey)}
            providerFields={providerFieldsByObject[activeObject.key] || []}
            providerFieldsLoading={providerFieldsLoadingByObject[activeObject.key] || false}
            providerCollections={providerCollectionsByObject[activeObject.key] || []}
            selectedCollection={selectedCollectionByObject[activeObject.key] || null}
            onCollectionChange={(key) => handleCollectionChange(activeObject.key, key)}
          />
        ) : (
          <SimpleObjectTab
            object={activeObject}
            integrationName={provider.label}
            onUpdate={(updates) => handleUpdateObject(activeObject.key, updates)}
          />
        ))}

      {/* Sync CTA */}
      {singleSyncCta ? (
        <div className="mt-10 pt-6 border-t border-border flex items-center justify-between gap-3">
          <button
            type="button"
            className="inline-flex h-9 items-center justify-center rounded-[5px] border border-accent-border bg-primary px-5 text-[13px] font-medium text-primary-foreground shadow-[0_3px_6px_rgba(0,0,0,0.02)] transition-colors hover:bg-accent-hover disabled:pointer-events-none disabled:opacity-50"
            disabled={!canSyncActiveObject || mappingConfig.syncing || savingConfiguration || activeObjectHasConflicts}
            onClick={() => { handleSync(activeObject?.key).catch(console.error); }}
          >
            {mappingConfig.syncing || savingConfiguration
              ? "Syncing..."
              : `Sync my ${provider.label} data with Hyperengage`}
          </button>
          {canGoToNextObject ? (
            <button
              type="button"
              className="text-[12px] text-primary hover:underline inline-flex items-center gap-1 disabled:pointer-events-none disabled:opacity-50"
              onClick={() => {
                if (nextObject) setActiveTab(nextObject.key);
              }}
            >
              {nextObjectCtaLabel} <Settings2 className="h-3 w-3" />
            </button>
          ) : (
            <span />
          )}
        </div>
      ) : (
        <>
          <div className="mt-10 pt-6 border-t border-border flex items-center justify-between">
            <button
              type="button"
              className="inline-flex h-9 items-center justify-center rounded-[5px] border border-accent-border bg-primary px-5 text-[13px] font-medium text-primary-foreground shadow-[0_3px_6px_rgba(0,0,0,0.02)] transition-colors hover:bg-accent-hover disabled:pointer-events-none disabled:opacity-50"
              disabled={!canSyncActiveObject || mappingConfig.syncing || savingConfiguration || activeObjectHasConflicts}
              onClick={() => { handleSync(activeObject?.key).catch(console.error); }}
            >
              {mappingConfig.syncing || savingConfiguration ? "Syncing..." : `Sync my ${provider.label} data with Hyperengage`}
            </button>

            {hasAnyEnabled ? (
              <button
                type="button"
                className="text-[12px] text-primary hover:underline inline-flex items-center gap-1"
                onClick={() => {
                  if (mappingConfig.objects.some((obj) => obj.key === "contacts")) {
                    setActiveTab("contacts");
                  }
                }}
              >
                Configure user/contact sync <Settings2 className="h-3 w-3" />
              </button>
            ) : (
              <span />
            )}
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="inline-flex h-8 items-center justify-center rounded-[5px] border border-border-chip bg-card px-3 text-[12px] font-medium text-secondary-foreground transition-colors hover:bg-muted disabled:pointer-events-none disabled:opacity-50"
                disabled={!hasAnyEnabled || savingConfiguration}
                onClick={() => { handleSaveConfiguration({ showSuccessToast: true }).catch(console.error); }}
              >
                {savingConfiguration ? "Saving..." : "Save configuration"}
              </button>
              <p
                className={`text-[12px] ${
                  hasConflicts
                    ? "text-status-cancelled"
                    : hasDraftChanges
                      ? "text-[var(--mantine-color-orange-7)]"
                      : "text-muted-foreground"
                }`}
              >
                {hasConflicts
                  ? "Resolve duplicate field selections before saving."
                  : hasDraftChanges
                    ? "You have unsaved changes."
                    : "All changes saved."}
              </p>
            </div>

            {showDeleteActions && activeObject && inboundSyncIdBySlug[activeObject.key] ? (
              <button
                type="button"
                className="text-[12px] text-status-cancelled hover:underline inline-flex items-center"
                onClick={() => {
                  setSyncToDelete(activeObject.key);
                  setDeleteConfirmOpened(true);
                }}
              >
                Delete {activeObject.label} sync
              </button>
            ) : (
              <span />
            )}
          </div>
        </>
      )}

      {/* Delete confirmation modal */}
      <Modal
        opened={deleteConfirmOpened}
        onClose={() => setDeleteConfirmOpened(false)}
        title="Delete Sync Configuration"
      >
        <Stack>
          <Text size="sm">
            Are you sure you want to delete this sync configuration? This action cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDeleteConfirmOpened(false)}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={async () => {
                if (syncToDelete && workspace?.id) {
                  try {
                    // Disable integration.app flows first
                    await handleDeactivateSync(syncToDelete).catch(() => {});
                    if (writebackByObject[syncToDelete]?.enabled) {
                      await handleDeactivateWritebackSync(syncToDelete).catch(() => {});
                    }

                    // Delete writeback sync config if exists
                    const wbId = writebackSyncIdBySlug[syncToDelete];
                    if (wbId) {
                      await deleteSyncConfigMutation.mutateAsync({
                        workspace_id: workspace.id,
                        sync_id: wbId,
                      });
                    }

                    // Delete inbound sync config
                    const inboundId = inboundSyncIdBySlug[syncToDelete];
                    if (inboundId) {
                      await deleteSyncConfigMutation.mutateAsync({
                        workspace_id: workspace.id,
                        sync_id: inboundId,
                      });
                    }

                    handleUpdateObject(syncToDelete, { enabled: false });
                    toast.success("Sync deleted", {
                      description: `Removed ${syncToDelete} sync configuration`,
                    });
                  } catch (error) {
                    console.error("Error deleting sync:", error);
                    toast.error("Delete failed", {
                      description: "Could not fully remove sync configuration",
                    });
                  }
                }
                setDeleteConfirmOpened(false);
                setSyncToDelete(null);
              }}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
